import { Component, Input } from '@angular/core';
import {
  PowerFlowGraphMode,
  System,
  WeatherService,
  getTimeRemoveAndApplyOffset,
  isNotNullOrUndefined,
} from 'ngx-core-services';
import { combineLatest, BehaviorSubject, Subject, Observable } from 'rxjs';
import { switchMap, shareReplay, map, tap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { SystemsState, requestSystemPowerState, selectedSystemMetadata, selectedSystemUid, selectedSystem, selectAzzurroHubHasActiveSolarSensor, selectedSystemVirtualRTStatus } from '../../store';
import {  selectSystemPowerStateBySelectedSystem } from '../../store/selectors/flow-chart.selectors';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';

const getLabels = () => ({
  home: 'Home',
  grid: 'Grid',
  solar: 'Solar',
  battery: 'Battery',
  loading: 'Loading...',
  disconnected: 'Disconnected',
  unit: 'Unit',
  title: 'Power Overview',
  irradiance: 'Irradiance',
  panelTemp: 'Temperature',
});

const FLOW_GRAPH_ANIMATION_TIMER = 2;
type FlowChartPowerUnit = 'W' | 'kW';

@CreateBlockReduxSupport()
@Component({
  selector: 'lib-flow-chart',
  templateUrl: './flow-chart.component.html',
  styleUrls: ['./flow-chart.component.scss'],
})
export class FlowChartComponent implements IReduxBlock {

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  private _language = "en";
  @Input() set languages(value: string) {
    this._language = value;
  }
  get languages() {
    return this._language;
  }

  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  @Input() showWeather = true;
  @Input() showLastUpdated = true;
  @Input() showIrradianceInfo = true;
  @Input() powerUnit: FlowChartPowerUnit = 'W';

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly duration = FLOW_GRAPH_ANIMATION_TIMER;
  readonly mode?: PowerFlowGraphMode;

  readonly requestSystemPowerState$ = combineLatest([
    this._refresh$,
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this._instanceId$,
  ]).pipe(
    tap(([forceRefresh, systemId, instanceId]) => {
      this.store.dispatch(requestSystemPowerState({ systemId, instanceId, forceRefresh }));
    }
  ));

  readonly systemPowerState$ = this._instanceId$.pipe(
    switchMap((instanceId) => this.store.select(selectSystemPowerStateBySelectedSystem(instanceId)).pipe(isNotNullOrUndefined())),
  );

  readonly systemMetadata$ = this.store.select(selectedSystemMetadata).pipe(isNotNullOrUndefined());

  readonly isConnected$ = this.systemMetadata$.pipe(
    map((metadata) => metadata?.isConnected ?? true),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isAzzurroHub$ = this.store.select(selectedSystem).pipe(
    map((system) => system?.monitoringType === 'AzzurroHub'),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly isRTActive$ = this.store.select(selectedSystemVirtualRTStatus).pipe(
    map((status) => Boolean(status?.active)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly hasSolarimeter$ = this.store.select(selectAzzurroHubHasActiveSolarSensor).pipe(
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly flowGraphMode$ = this.systemMetadata$.pipe(
    map((metadata) => metadata?.structure),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly tz$ = this.systemMetadata$.pipe(
    map((metadata) => metadata?.tz),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly lastUpdate$ = combineLatest([
    this.systemPowerState$,
    this.systemMetadata$.pipe(
      map((metadata) => metadata?.tz),
      isNotNullOrUndefined(),
    ),
  ]).pipe(
    map(([powerStats, timezone]) => {
      if (!powerStats?.ts || isNaN(powerStats.ts)) {
        return undefined;
      }

      return getTimeRemoveAndApplyOffset(powerStats.ts , (timezone) * 4);
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly location$ = this.systemMetadata$.pipe(
    map((metadata) => metadata?.location),
    isNotNullOrUndefined(),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly weatherInfo$ = this.location$.pipe(
    isNotNullOrUndefined(),
    switchMap((location) =>
      this.weatherService.getWeatherInfoByLocation(location, this.languages)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  constructor(
    private readonly weatherService: WeatherService,
    readonly store: Store<SystemsState>,
  ) { }

  get powerUnitSymbol(): FlowChartPowerUnit {
    return this.powerUnit === 'kW' ? 'kW' : 'W';
  }

  get powerPrecision(): number {
    return this.powerUnit === 'kW' ? 2 : 0;
  }

  private get unitScale(): number {
    return this.powerUnit === 'kW' ? 1000 : 1;
  }

  scalePower(value?: number): number | undefined {
    if (value === null || value === undefined) {
      return undefined;
    }
    return value / this.unitScale;
  }

}
